import { json } from '@remix-run/node';
import type { LoaderFunction } from '@remix-run/node';
import {
  Links,
  Meta,
  MetaFunction,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useLocation,
  useMatches,
  useNavigation,
} from '@remix-run/react';
import type { LinksFunction } from '@remix-run/node';
import { AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';
import './tailwind.css';
import Header from './components/header';
import Footer from './components/Footer';
import styles from './styles/globals.css';
import fonts from './styles/fonts.css';
import LoadingIndicator from './components/LoadingIndicator';

// Google Fonts와 같은 외부 리소스 링크를 정의하는 함수

export const meta: MetaFunction = () => {
  return [
    { title: "법무법인 휘상 | 형사, 금융, 부동산, 경영권 분쟁, 민사, 가사, 성범죄 전문" },
    { name: "description", content: "법무법인 휘상은 형사사건, 금융 자문, 부동산PF, 경영권분쟁, 성범죄, 가사 사건, 민사 사건에서 전문적인 법률서비스를 제공합니다." },
    { name: "keywords", content: "법무법인, 변호사, 법률 상담, 형사 사건, 금융 자문, 금융 송무, 부동산 PF, 경영권 분쟁, 단체법, 상속, 이혼, 가사 분쟁, 손해배상, 민사사건, 성범죄, 성폭력, 성추행, 법무법인 휘상" },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://jp-hwisang.co.kr" },
    { property: "og:title", content: "법무법인 휘상 | 형사, 금융, 부동산, 경영권 분쟁, 가사 전문" },
    { property: "og:description", content: "법무법인 휘상은 형사사건, 금융 자문, 부동산PF, 경영권분쟁, 성범죄, 가사 사건, 민사 사건에서 전문적인 법률서비스를 제공합니다." },
    { property: "og:image", content: "https://jp-hwisang.co.kr/images/og-image.jpg" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "법무법인 휘상 | 형사, 금융, 부동산, 경영권 분쟁, 성범죄, 가사 전문" },
    { name: "twitter:description", content: "법무법인 휘상은 형사사건, 금융 자문, 부동산PF, 경영권분쟁, 성범죄, 가사 사건, 민사 사건에서 전문적인 법률서비스를 제공합니다." },
    { name: "twitter:image", content: "https://jp-hwisang.co.kr/images/og-image.jpg" },
  ];
};

export const links: LinksFunction = () => {
  return [
    // 폰트 최적화
    { rel: "preconnect", href: "https://fonts.googleapis.com" },
    { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
    // 폰트 preload - 중요한 폰트만 미리 로드
    {
      rel: "preload",
      href: "https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@400;500;700&display=swap",
      as: "style"
    },
    // 최적화된 폰트 로딩 - font-display: swap 적용
    {
      rel: "stylesheet",
      href: "https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@400;500;700&display=swap"
    },
    // { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
    // { rel: "icon", type: "image/png", href: "/images/cube.png" },
    // { rel: "apple-touch-icon", href: "/images/logo.png" },
    // { rel: "shortcut icon", type: "image/x-icon", href: "/favicon.ico" },
    { rel: "icon", href: "/images/cube.png" },
    { rel: "canonical", href: "https://jp-hwisang.co.kr" },
    {
      rel: 'stylesheet',
      href: '/css/styles.css',
    },
  ];
};

export const loader: LoaderFunction = async ({ request }) => {
  const url = new URL(request.url);
  const searchParams = new URLSearchParams(url.search);
  const langParam = searchParams.get('lang');

  // 일본 사이트 감지:
  // 1. ja.hwisang.kr 또는 ja.hwisang.co.kr 도메인
  // 2. ?lang=ja 파라미터 (로컬 테스트용)
  // 3. URL 경로가 /ja로 시작하는 경우
  const isJapanSite = langParam === 'ja' ||
    url.pathname.startsWith('/ja');

  return json({
    KAKAO_MAP_API_KEY: process.env.KAKAO_MAP_API_KEY,
    isJapanSite,
  }, {
    headers: {
      // 브라우저와 CDN 캐싱 설정 - 60초 동안 캐싱
      'Cache-Control': 'public, max-age=60, s-maxage=120',
      // 콘텐츠 보안 정책 - 카카오맵과 Supabase를 허용하기 위함
      'Content-Security-Policy': "default-src 'self'; script-src 'self' 'unsafe-inline' 'unsafe-eval' https://dapi.kakao.com http://dapi.kakao.com https://*.daumcdn.net http://*.daumcdn.net http://t1.daumcdn.net https://t1.daumcdn.net https://*.kakao.com http://*.kakao.com https://www.googletagmanager.com https://www.google-analytics.com http://cdn.smlog.co.kr https://cdn.smlog.co.kr; style-src 'self' 'unsafe-inline' https://fonts.googleapis.com; font-src 'self' https://fonts.gstatic.com; img-src 'self' data: https://*.daumcdn.net http://*.daumcdn.net https://*.kakao.com http://*.kakao.com https://*.supabase.co https://www.google-analytics.com https://www.googletagmanager.com; connect-src 'self' https://dapi.kakao.com http://dapi.kakao.com http://localhost:8002 ws://localhost:8002 ws: wss: https://*.supabase.co https://www.google-analytics.com https://www.googletagmanager.com https://analytics.google.com http://cdn.smlog.co.kr https://cdn.smlog.co.kr; media-src 'self' https://*.supabase.co;"
    }
  });
};

// RouteHandle 인터페이스 추가
interface RouteHandle {
  disableRootLayout?: boolean;
}

// 앱의 루트 컴포넌트
export default function App() {
  const { KAKAO_MAP_API_KEY, isJapanSite } = useLoaderData<typeof loader>();
  const location = useLocation();
  const navigation = useNavigation();
  const [isHydrated, setIsHydrated] = useState(false);
  const matches = useMatches();

  // 현재 라우트의 handle 객체를 확인하여 레이아웃 비활성화 여부 확인
  // 일본 사이트면 한국 레이아웃(Header/Footer) 비활성화
  const shouldDisableRootLayout = matches.some(
    match => match.handle && (match.handle as RouteHandle).disableRootLayout
  ) || isJapanSite;

  // 클라이언트 측 하이드레이션이 완료되면 상태 업데이트
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // 네비게이션 상태 확인 (로딩 중인지)
  const isNavigating = navigation.state !== 'idle';

  return (
    // HTML 문서 시작, 한국어 설정
    <html lang="ko" className="light">
      {/* head 태그 내부 - 메타 정보와 리소스 링크 */}
      <head>
        {/* 문자 인코딩 설정 */}
        <meta charSet="utf-8" />
        {/* 모바일 반응형을 위한 뷰포트 설정 */}
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {/* 네이버 메타 태그 */}
        <meta name="naver-site-verification" content="944cfa005740720739e169ba6bf7a90af30ee011" />
        <link rel="canonical" href="https://jp-hwisang.co.kr" />
        {/* 다크모드 설정 무시 - 항상 라이트모드 사용 */}
        <meta name="color-scheme" content="light only" />
        {/* 색상 강제 변환 비활성화 */}
        <meta name="theme-color" content="#ffffff" />
        <meta name="forced-colors" content="none" />
        <meta name="supported-color-schemes" content="light" />
        {/* 삼성 브라우저 다크모드 비활성화 */}
        <meta name="nightmode" content="disable" />
        <meta name="samsung:force_dark_mode" content="false" />
        <style dangerouslySetInnerHTML={{
          __html: `
          /* 시스템 다크모드 방지 */
          @media (prefers-color-scheme: dark) {
            html { 
              color-scheme: light !important;
            }
          }
          
          /* 브라우저의 강제 어둡게 보기 방지 - 더 강력한 방식 */
          html {
            background-color: white !important;
            color-scheme: light !important;
          }
          
          html, body, div, span, applet, object, iframe,
          h1, h2, h3, h4, h5, h6, p, blockquote, pre,
          a, abbr, acronym, address, big, cite, code,
          del, dfn, em, img, ins, kbd, q, s, samp,
          small, strike, strong, sub, sup, tt, var,
          b, u, i, center,
          dl, dt, dd, ol, ul, li,
          fieldset, form, label, legend,
          table, caption, tbody, tfoot, thead, tr, th, td,
          article, aside, canvas, details, embed, 
          figure, figcaption, footer, header, hgroup, 
          menu, nav, output, ruby, section, summary,
          time, mark, audio {
            -webkit-filter: none !important;
            filter: none !important;
            -ms-high-contrast-adjust: none !important;
            forced-color-adjust: none !important;
          }
          
          /* 비디오 필터 제외 - 원래 상태 유지 */
          video {
            -ms-high-contrast-adjust: none !important;
            forced-color-adjust: none !important;
          }
          
          /* 삼성 브라우저 특화 */
          @media screen and (-webkit-min-device-pixel-ratio:0) {
            body {
              -webkit-appearance: none;
              -webkit-text-fill-color: initial !important;
            }
          }
        `}} />
        {/* CSP 정책 메타 태그 - 더 완화된 정책 */}
        <meta
          httpEquiv="Content-Security-Policy"
          content="default-src 'self'; script-src 'self' 'unsafe-inline' 'unsafe-eval' https://dapi.kakao.com http://dapi.kakao.com https://*.daumcdn.net http://*.daumcdn.net http://t1.daumcdn.net https://t1.daumcdn.net https://*.kakao.com http://*.kakao.com; style-src 'self' 'unsafe-inline' https://fonts.googleapis.com; font-src 'self' https://fonts.gstatic.com; img-src 'self' data: https://*.daumcdn.net http://*.daumcdn.net https://*.kakao.com http://*.kakao.com https://*.supabase.co; connect-src 'self' https://dapi.kakao.com http://dapi.kakao.com http://localhost:8002 ws://localhost:8002 ws: wss: https://*.supabase.co; media-src 'self' https://*.supabase.co;"
        />
        {/* Remix의 메타 태그 컴포넌트 (title, description 등) */}
        <Meta />
        {/* 위에서 정의한 links (폰트 등) 적용 */}
        <Links />
        {process.env.NODE_ENV === "production" && (
          <>
            <script
              async
              src={`https://www.googletagmanager.com/gtag/js?id=G-XXXXXXXXXX`}
            />
            <script
              dangerouslySetInnerHTML={{
                __html: `
                  window.dataLayer = window.dataLayer || [];
                  function gtag(){dataLayer.push(arguments);}
                  gtag('js', new Date());
                  gtag('config', 'G-XXXXXXXXXX');
                `,
              }}
            />
          </>
        )}

        {/* Schema.org 구조화된 데이터 */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "LegalService",
              "name": "법무법인 휘상",
              "description": "법무법인 휘상은 형사사건, 금융 자문, 부동산PF, 경영권분쟁, 성범죄, 가사 사건, 민사 사건에서 전문적인 법률서비스를 제공합니다.",
              "url": "https://jp-hwisang.co.kr",
              "logo": "https://jp-hwisang.co.kr/images/cube.png",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "서울 강남구 테헤란로 211 한국고등교육재단빌딩 15층",
                "addressLocality": "서울",
                "addressRegion": "강남구",
                "postalCode": "06141",
                "addressCountry": "KR"
              },
              "telephone": "02-6959-2170",
              "email": "hwisang@hwisang.co.kr",
              "serviceType": [
                "형사 사건",
                "금융 자문 및 송무",
                "부동산 PF",
                "경영권 분쟁",
                "상속 및 이혼 등 가사 분쟁",
                "손해배상 등 민사사건",
                "성범죄",
                "성폭력",
              ],
              "openingHours": "Mo,Tu,We,Th,Fr 09:00-18:00",
              "sameAs": [
                "https://www.instagram.com/hwisang_lawfirm",
                "https://blog.naver.com/hwisang"
              ]
            })
          }}
        />
      </head>

      {/* body 태그 내부 - 실제 컨텐츠 */}
      <body className="min-h-screen flex flex-col bg-white text-black">
        {/* 헤더 컴포넌트 - 네비게이션 메뉴 (레이아웃 비활성화 시 제외) */}
        {!shouldDisableRootLayout && <Header />}

        {/* 글로벌 로딩 인디케이터 */}
        <LoadingIndicator isVisible={isNavigating} />

        {/* 메인 컨텐츠 영역 - 헤더 아래 여백 조정 */}
        <main className={`flex-grow ${!shouldDisableRootLayout ? '' : ''}`}>
          <AnimatePresence mode="wait" initial={false}>
            <Outlet />
          </AnimatePresence>
        </main>

        {/* 푸터 컴포넌트 (레이아웃 비활성화 시 제외) */}
        {!shouldDisableRootLayout && <Footer />}

        {/* Remix의 스크롤 복원 기능 - 페이지 전환 시 스크롤 위치 유지 */}
        <ScrollRestoration />

        {/* Remix의 클라이언트 사이드 스크립트 - 하이드레이션에 필요 */}
        <Scripts />

        {/* 환경 변수 - API 키 설정 */}
        <script
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify({
              KAKAO_MAP_API_KEY,
            })}`,
          }}
        />
      </body>
    </html>
  );
}
